// One page, four states: start, lesen, fragen, befund, and the wait step between lesen and fragen. A run lives at
// /r/<id>: a reload resumes it, the link can be passed on and opens the run where it stands, and / is the start
// form. A website with a run of the last 24 hours opens that run; «Neu starten» in the header leaves an unfinished
// one behind. Nothing is kept in the browser.
import type { AbsichtElement, Answer, RunEvent, RunView } from '../shared/types.js';
import { ApiError, api } from './api.js';
import { boot, clear, h, scrollTop, t } from './dom.js';
import { renderBefund } from './stage-befund.js';
import { renderFragen } from './stage-fragen.js';
import { renderLesen, type LesenStage } from './stage-lesen.js';
import { renderStart } from './stage-start.js';
import { renderVorbereiten } from './stage-vorbereiten.js';

type Stage = 'start' | 'lesen' | 'vorbereiten' | 'fragen' | 'befund';

const RUN_PATH = /^\/r\/([A-Za-z0-9_-]{6,40})\/?$/;
const app = document.getElementById('app')!;
const progress = [document.getElementById('p1'), document.getElementById('p2'), document.getElementById('p3')];
const restart = document.getElementById('restart')!;

let runId: string | null = null;
let view: RunView | null = null;
let source: EventSource | null = null;
let pollTimer: number | null = null;
let retryTimer = 0;
let lastSeq = 0;
/** Counts the runs opened from the address: only the answer to the latest request is shown. */
let opened = 0;
let lesenStage: LesenStage | null = null;
/** Why the reading stage is back on screen after «Weiter»; shown once. */
let lesenNotice: string | null = null;

document.getElementById('wordmark')!.textContent = boot.config.brand.tool_name;
document.getElementById('langlabel')!.textContent = t('meta.language_label');
restart.textContent = t('header.restart');
restart.addEventListener('click', restartRun);

function setProgress(stage: Stage): void {
  // The wait for the Rückfragen already counts as the second part: «Weiter» moved the visitor on.
  const n = { start: 0, lesen: 1, vorbereiten: 2, fragen: 2, befund: 3 }[stage];
  progress.forEach((el, i) => el?.classList.toggle('on', i < n));
}

/** The address follows the run. A new run adds a history entry, so «back» leads to the start screen. */
function setPath(path: string, replace = false): void {
  if (location.pathname === path && !location.search) return;
  if (replace) history.replaceState(null, '', path);
  else history.pushState(null, '', path);
}

function stopStream(): void {
  source?.close();
  source = null;
  window.clearTimeout(retryTimer);
  if (pollTimer) window.clearInterval(pollTimer);
  pollTimer = null;
}

function mount(stage: Stage, el: HTMLElement): void {
  clear(app);
  app.append(el);
  // The stylesheet fits the reading stage to one screen; the other stages scroll.
  document.body.dataset.stage = stage;
  setProgress(stage);
  offerRestart(stage);
  scrollTop();
}

/** «Neu starten» in the header, while an unfinished run rests: read, or at the Rückfragen. Not while we read or
 * write, and not on a Befund: that one stays the answer for 24 hours. */
function offerRestart(stage: Stage | null): void {
  restart.hidden = !(stage === 'fragen' || (stage === 'lesen' && view?.status === 'gelesen'));
}

/** Leads to the start form with the address of the run in the box. Nothing is lost before «Website lesen»: the run
 * stays at its address, and «back» leads there. */
function restartRun(): void {
  if (!view) return;
  const address = view.origin;
  setPath('/');
  showStart(null, true, address);
}

/** The start screen. It lives at /, so a run that is gone or has failed does not stay in the address;
 * `keepPath` leaves the address alone when only the request failed and a reload may try again. `restartOf` is the
 * address of the run left with «Neu starten»: the start that follows asks for a new run, not for the stored one. */
function showStart(message: { kind: 'error' | 'info'; text: string } | null = null, keepPath = false, restartOf: string | null = null): void {
  stopStream();
  runId = null;
  view = null;
  lesenStage = null;
  if (!keepPath) setPath('/', true);
  mount('start', renderStart({ start: (url, consent) => startRun(url, consent, restartOf !== null) }, message, restartOf));
}

async function startRun(url: string, consent: boolean, anew = false): Promise<void> {
  const result = await api.start({ url, consent, fresh: anew });
  const fresh = await api.run(result.id);
  // «Befund vom …»: only when this start returned the stored Befund of the last 24 hours.
  fresh.cached = result.cached;
  runId = result.id;
  view = fresh;
  setPath(`/r/${runId}`);
  route();
}

async function startFixture(name: string): Promise<void> {
  const result = await api.start({ fixture: name });
  view = await api.run(result.id);
  runId = result.id;
  // Replaces /?fixture=…: «back» must not start another sample run.
  setPath(`/r/${runId}`, true);
  route();
}

function route(): void {
  if (!view || !runId) return showStart();
  // A draft belongs to the run of its stage, whatever is on screen by the time it is sent.
  const id = runId;
  if (view.status === 'error') return showStart({ kind: 'error', text: view.error?.message ?? t('start.errors.generic') });
  if (view.status === 'deleted') return showStart({ kind: 'info', text: t('befund.deleted') });
  if (view.status === 'befund' && view.befund) {
    stopStream();
    return mount('befund', renderBefund(view, befundActions));
  }
  if (view.status === 'fragen' && view.questions) {
    stopStream();
    return mount('fragen', renderFragen(view, { submit: submitAnswers, draft: (fragen) => api.draft(id, { fragen }) }));
  }
  // lesen or gelesen
  if (view.status === 'lesen') {
    // The event stream replays a run in work from its first event; the stage starts empty, so no line shows twice.
    lastSeq = 0;
    view.stream = [];
  }
  lesenStage = renderLesen(view, { proceed: (description) => void proceed(description), draft: (description) => api.draft(id, { description }) }, lesenNotice);
  lesenNotice = null;
  mount('lesen', lesenStage.el);
  if (view.status === 'lesen') subscribe();
}

function applyEvent(event: RunEvent): void {
  if (event.seq) lastSeq = Math.max(lastSeq, event.seq);
  if (!lesenStage || !view) return;
  if (event.type === 'status' && event.list) {
    view.list = { ...view.list, ...event.list };
    lesenStage.onStatus(view.list);
  } else if (event.type === 'observation' && event.item) {
    view.stream.push(event.item);
    lesenStage.onItem(event.item);
  } else if (event.type === 'done') {
    view.status = 'gelesen';
    lesenStage.onDone();
    offerRestart('lesen');
    stopStream();
    void refreshView();
  } else if (event.type === 'error') {
    view.status = 'error';
    lesenStage.onError(event.message ?? t('lesen.errors.pipeline'));
    stopStream();
  }
}

async function refreshView(): Promise<void> {
  const id = runId;
  if (!id) return;
  try {
    const fresh = await api.run(id);
    // The visitor may have moved on while the request was under way.
    if (runId !== id) return;
    view = fresh;
    if (fresh.status !== 'lesen' && fresh.status !== 'gelesen') route();
  } catch {
    // keep the current view
  }
}

function subscribe(): void {
  const id = runId;
  if (!id) return;
  stopStream();
  let failures = 0;
  const open = () => {
    source = new EventSource(`/api/run/${encodeURIComponent(id)}/events?after=${lastSeq}`);
    for (const type of ['status', 'observation', 'done', 'error']) {
      source.addEventListener(type, (message) => {
        try {
          applyEvent(JSON.parse((message as MessageEvent).data) as RunEvent);
        } catch {
          // ignore malformed events
        }
      });
    }
    source.onerror = () => {
      failures += 1;
      source?.close();
      source = null;
      if (failures >= 2) startPolling();
      else retryTimer = window.setTimeout(open, 1500);
    };
  };
  if (typeof EventSource === 'undefined') startPolling();
  else open();
}

function startPolling(): void {
  if (pollTimer || !runId) return;
  pollTimer = window.setInterval(async () => {
    const id = runId;
    if (!id || !view || !lesenStage) return;
    try {
      const fresh = await api.run(id);
      if (runId !== id || !view || !lesenStage) return;
      const known = view.stream.length;
      for (const item of fresh.stream.slice(known)) {
        view.stream.push(item);
        lesenStage.onItem(item);
      }
      view.list = fresh.list;
      lesenStage.onStatus(fresh.list);
      if (fresh.status === 'gelesen') {
        view.status = 'gelesen';
        lesenStage.onDone();
        offerRestart('lesen');
        stopStream();
      } else if (fresh.status === 'error') {
        view.status = 'error';
        lesenStage.onError(fresh.error?.message ?? t('lesen.errors.pipeline'));
        stopStream();
      } else if (fresh.status !== 'lesen') {
        view = fresh;
        stopStream();
        route();
      }
    } catch (error) {
      if (runId === id && error instanceof ApiError && (error.status === 404 || error.status === 410)) showStart(goneMessage(error));
    }
  }, 2500);
}

/** A run that is no longer there: deleted by a visitor, or swept after the retention period. */
function goneMessage(error: ApiError): { kind: 'info'; text: string } {
  return { kind: 'info', text: t(error.status === 410 ? 'befund.deleted' : 'befund.full.expired') };
}

/** «Weiter»: the wait for the Rückfragen is a step of its own. The reading stage comes back only when the request
 * fails, with the reason and with what was in the description box. */
async function proceed(description: string | null): Promise<void> {
  const id = runId;
  if (!id || !view || !lesenStage) return;
  const box = lesenStage.draft();
  lesenStage = null;
  mount('vorbereiten', renderVorbereiten());
  try {
    const fresh = await api.proceed(id, description);
    if (runId !== id) return;
    view = fresh;
  } catch (error) {
    if (runId !== id || !view) return;
    if (error instanceof ApiError && (error.status === 404 || error.status === 410)) return showStart(goneMessage(error));
    view.draft = { description: box, fragen: view.draft?.fragen ?? null };
    lesenNotice = (error as { message?: string }).message || t('lesen.errors.pipeline');
  }
  route();
}

async function submitAnswers(answers: Answer[], absicht: AbsichtElement[] | null): Promise<void> {
  const id = runId;
  if (!id) return;
  // The Befund is being written: «Neu starten» is back when the request fails and the questions return.
  offerRestart(null);
  let fresh: RunView;
  try {
    fresh = await api.answers(id, answers, absicht);
  } catch (error) {
    if (runId === id) offerRestart('fragen');
    throw error;
  }
  if (runId !== id) return;
  view = fresh;
  route();
}

const befundActions = {
  async sendEmail(email: string, marketing: boolean) {
    if (!runId) throw new Error('no run');
    const result = await api.email(runId, email, marketing);
    if (view) view.email_sent = result.ok;
    return result;
  },
  cta() {
    if (runId) void api.cta(runId).catch(() => undefined);
  },
  async remove() {
    if (!runId) return;
    await api.remove(runId);
    showStart({ kind: 'info', text: t('befund.deleted') });
  }
};

/** Opens what the address names: the run at /r/<id>, the start screen anywhere else. */
async function open(): Promise<void> {
  const ticket = ++opened;
  const id = RUN_PATH.exec(location.pathname)?.[1] ?? null;
  if (!id) return showStart();
  stopStream();
  try {
    const fresh = await api.run(id);
    if (ticket !== opened) return;
    runId = id;
    view = fresh;
  } catch (error) {
    if (ticket !== opened) return;
    if (error instanceof ApiError && (error.status === 404 || error.status === 410)) return showStart(goneMessage(error));
    // A failed request is not a dead link: the address stays, a reload tries again.
    return showStart({ kind: 'error', text: t('start.errors.generic') }, true);
  }
  route();
}

async function init(): Promise<void> {
  if (boot.token) {
    if (boot.token === 'invalid') return mount('befund', h('section', { class: 'stage on' }, h('p', { class: 'notice' }, t('befund.full.expired'))));
    try {
      view = await api.befund(boot.token);
      runId = view.id;
      return mount('befund', renderBefund(view, befundActions));
    } catch {
      return mount('befund', h('section', { class: 'stage on' }, h('p', { class: 'notice' }, t('befund.full.expired'))));
    }
  }
  if (boot.fixture) {
    try {
      await startFixture(boot.fixture);
      return;
    } catch {
      return showStart({ kind: 'error', text: t('start.errors.generic') });
    }
  }
  await open();
}

// «Back» and «forward» between the start screen and the runs of this visit. The full Befund at /b/<token> is
// a page of its own and never part of that history.
window.addEventListener('popstate', () => {
  if (!location.pathname.startsWith('/b/')) void open();
});

void init();
